import { useState, useEffect, useCallback } from 'react';
import { Customer, CustomerListResponse, CustomerSearchParams, CustomerLimitOption } from '../models/CustomerModels';
import { CustomerService } from '../services/CustomerService';
import { formatDateTimeKST } from '../utils/dateUtils';

// 고객 관리 ViewModel 훅
export const useCustomerViewModel = () => {
  // 상태 관리
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit, setLimit] = useState<CustomerLimitOption>(15);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);

  // 고객 목록 로드
  const loadCustomers = useCallback(async (params?: CustomerSearchParams) => {
    try {
      setLoading(true);
      setError(null);

      const searchParams: CustomerSearchParams = {
        page: params?.page || currentPage,
        limit: params?.limit || limit,
        q: params?.q !== undefined ? params.q : searchQuery
      };

      console.log('고객 목록 로드 시작:', searchParams);

      // 실제 API 호출 시도, 실패하면 목업 데이터 사용
      let response: CustomerListResponse;
      try {
        response = await CustomerService.getCustomers(searchParams);
        console.log('고객 목록 API 응답 성공:', response);
      } catch (apiError) {
        setError('고객 목록을 불러오는데 실패했습니다.');
        console.error('고객 목록 조회 실패:', apiError);
        return;
      }

      console.log('📊 ViewModel: API 응답 받음:', {
        customers: response.customers,
        customersLength: response.customers?.length,
        total: response.total,
        page: response.page,
        limit: response.limit,
        totalPages: response.totalPages
      });

      // customers가 배열인지 확인하고 안전하게 설정
      const safeCustomers = Array.isArray(response.customers) ? response.customers : [];

      setCustomers(safeCustomers);
      setTotal(response.total || 0);
      setTotalPages(response.totalPages || 1);
      setCurrentPage(response.page || 1);
      setLimit((response.limit as CustomerLimitOption) || 15);

      console.log('📊 ViewModel: 상태 업데이트 완료:', {
        customersLength: response.customers?.length,
        total: response.total,
        page: response.page,
        limit: response.limit,
        totalPages: response.totalPages
      });

    } catch (err) {
      setError('고객 목록을 불러오는데 실패했습니다.');
      console.error('Customer list loading error:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, limit, searchQuery]);

  // 검색 핸들러
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // 검색 시 첫 페이지로 이동
    loadCustomers({ page: 1, limit, q: query });
  };

  // 페이지 변경 핸들러
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadCustomers({ page, limit, q: searchQuery });
  };

  // 페이지 크기 변경 핸들러
  const handleLimitChange = (newLimit: CustomerLimitOption) => {
    setLimit(newLimit);
    setCurrentPage(1); // 페이지 크기 변경 시 첫 페이지로 이동
    loadCustomers({ page: 1, limit: newLimit, q: searchQuery });
  };

  // 데이터 새로고침
  const refreshData = () => {
    loadCustomers();
  };

  // 날짜 포맷팅 유틸리티
  const formatDateTime = (dateTimeString: string) => formatDateTimeKST(dateTimeString);

  // 금액 포맷팅 유틸리티
  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('ko-KR') + '원';
  };

  // 마케팅 동의 상태 포맷팅
  const formatMarketingStatus = (status: boolean) => {
    return status ? '동의' : '미동의';
  };

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    loadCustomers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    // 상태
    customers,
    loading,
    error,
    searchQuery,
    currentPage,
    limit,
    totalPages,
    total,

    // 액션
    handleSearch,
    handlePageChange,
    handleLimitChange,
    refreshData,

    // 유틸리티
    formatDateTime,
    formatCurrency,
    formatMarketingStatus
  };
}; 